import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { User, Menu, Settings, LogOut, Plus, X, Image, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { 
  Dialog, 
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import ROUTES from '@/routes-map';
import NotificationBell from '@/components/notifications/NotificationBell';

const DoctorHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAddStoryModal, setShowAddStoryModal] = useState(false);
  const [storyImage, setStoryImage] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  
  const handleAddStoryClick = () => {
    fileInputRef.current?.click();
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setFileError(null);
    
    if (file) {
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setFileError('File size exceeds 5MB limit');
        return;
      }
      
      // Validate file type (image or video)
      const fileType = file.type.split('/')[0];
      if (fileType !== 'image' && fileType !== 'video') {
        setFileError('Only image or video files are allowed');
        return;
      }
      
      // Read and preview the file
      const reader = new FileReader();
      reader.onloadend = () => {
        setStoryImage(reader.result as string);
        setShowAddStoryModal(true);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleAddStory = () => {
    if (!storyImage) {
      toast({
        title: "Missing image",
        description: "Please upload an image or video for your story.",
        variant: "destructive"
      });
      return;
    }
    
    if (!caption.trim()) {
      toast({
        title: "Missing caption",
        description: "Please add a caption for your story.",
        variant: "destructive"
      });
      return;
    }
    
    setIsUploading(true);
    
    // In a real app, this would be an API call to add the story
    setTimeout(() => {
      // Reset the form
      resetAddStoryForm();
      
      toast({
        title: "Story added",
        description: "Your story has been successfully added and will be visible for 7 days.",
      });
      
      // In a real app, you would refresh stories or update state
    }, 1000);
  };
  
  const resetAddStoryForm = () => {
    setShowAddStoryModal(false);
    setStoryImage(null);
    setCaption('');
    setIsUploading(false);
    setFileError(null);
  };
  
  return (
    <header className="fixed top-0 left-0 right-0 bg-background/95 backdrop-blur-sm z-50 shadow-sm">
      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center">
          <Link to={ROUTES.DOCTOR.DASHBOARD} className="flex items-center gap-2">
            <h1 className="text-lg font-bold text-primary">ask.dentist</h1>
            <span className="text-sm text-muted-foreground">Doctor</span>
          </Link>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Messages */}
          <Link to={ROUTES.DOCTOR.PATIENT_CHAT.ROOT} className="p-1 rounded-full hover:bg-muted">
            <Button variant="ghost" size="icon" className="p-0 rounded-full">
              <Sheet>
                <SheetTrigger>
                  <div className="relative">
                    <MessageCircle className="h-6 w-6 text-primary" />
                    <span className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full"></span>
                  </div>
                </SheetTrigger>
              </Sheet>
            </Button>
          </Link>
          
          {/* Add Story Button */}
          <Button 
            variant="ghost" 
            size="icon" 
            className="p-0 rounded-full hover:bg-muted"
            onClick={handleAddStoryClick}
          >
            <Plus className="h-6 w-6 text-primary" />
            <span className="sr-only">Add Story</span>
          </Button>
          
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/jpeg,image/png,video/mp4"
            onChange={handleFileChange}
          />
          
          {/* Notifications */}
          <NotificationBell />
          
          {/* User Profile Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="p-0 rounded-full hover:bg-muted">
                <Avatar className="h-8 w-8">
                  <AvatarImage src="https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=400&h=400&fit=crop" alt="Doctor" />
                  <AvatarFallback>DR</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={`${ROUTES.DOCTOR.PROFILE}/1`} className="flex items-center cursor-pointer">
                  <User className="mr-2 h-4 w-4" />
                  <span>My Profile</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={ROUTES.DOCTOR.SETTINGS} className="flex items-center cursor-pointer">
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="flex items-center cursor-pointer">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log Out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Mobile Menu Button */}
          <Button variant="ghost" size="icon" className="md:hidden p-0 rounded-full hover:bg-muted">
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="p-0 md:hidden">
                  <Menu className="h-6 w-6" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[240px] sm:w-[300px]">
                <div className="flex flex-col gap-6 mt-6">
                  <Link to={ROUTES.DOCTOR.DASHBOARD} className="text-lg font-semibold" onClick={() => setIsMenuOpen(false)}>
                    Dashboard
                  </Link>
                  <Link to={ROUTES.DOCTOR.PATIENTS} className="text-lg font-semibold" onClick={() => setIsMenuOpen(false)}>
                    My Patients
                  </Link>
                  <Link to={ROUTES.DOCTOR.PATIENT_CHAT.ROOT} className="text-lg font-semibold" onClick={() => setIsMenuOpen(false)}>
                    Patient Chat
                  </Link>
                  <Link to={ROUTES.DOCTOR.STATISTICS} className="text-lg font-semibold" onClick={() => setIsMenuOpen(false)}>
                    Statistics
                  </Link>
                  <Link to={ROUTES.DOCTOR.ADS} className="text-lg font-semibold" onClick={() => setIsMenuOpen(false)}>
                    My Ads
                  </Link>
                  <Link to={`${ROUTES.DOCTOR.PROFILE}/1`} className="text-lg font-semibold" onClick={() => setIsMenuOpen(false)}>
                    My Profile
                  </Link>
                  <Link to={ROUTES.DOCTOR.SETTINGS} className="text-lg font-semibold" onClick={() => setIsMenuOpen(false)}>
                    Settings
                  </Link>
                  <Link to={ROUTES.DOCTOR.NOTIFICATIONS} className="text-lg font-semibold" onClick={() => setIsMenuOpen(false)}>
                    Notifications
                  </Link>
                </div>
              </SheetContent>
            </Sheet>
          </Button>
        </div>
      </div>
      
      {/* Add Story Modal */}
      <Dialog open={showAddStoryModal} onOpenChange={setShowAddStoryModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Story</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {storyImage && (
              <div className="w-full h-48 bg-muted rounded-md overflow-hidden">
                <img 
                  src={storyImage} 
                  alt="Story preview" 
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="caption">Caption (100 chars max)</Label>
              <Textarea
                id="caption"
                placeholder="Add a caption to your story..."
                value={caption}
                onChange={(e) => setCaption(e.target.value.slice(0, 100))}
                maxLength={100}
              />
              <div className="text-xs text-right text-muted-foreground">
                {caption.length}/100 characters
              </div>
            </div>
            
            {fileError && (
              <div className="text-red-500 text-sm">
                {fileError}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={resetAddStoryForm}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddStory}
              disabled={isUploading || !caption.trim() || !storyImage}
            >
              {isUploading ? 'Adding...' : 'Add Story'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
};

export default DoctorHeader;
